import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Shield, BookOpen } from "lucide-react";
import { fetchProvidersHealth } from "@/lib/api";

const DOT_STYLES = {
  ok: "bg-terracotta shadow-[0_0_8px_rgba(184,90,68,0.6)]",
  warn: "bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.8)] animate-pulse",
  error: "bg-red-400 shadow-[0_0_12px_rgba(248,113,113,0.9)] animate-pulse",
};

const HEALTH_LABELS = {
  ok: "Every provider healthy",
  warn: "Watch — one provider trending",
  error: "Action needed — provider unhealthy",
};

/**
 * A tiny toggle rendered in the header ONLY for authenticated users whose
 * email is on the server-side ADMIN_EMAILS allowlist. Everyone else sees
 * no toggle and no DOM element at all.
 *
 * Also renders a live "system health" dot to the LEFT of the toggle that
 * polls /api/admin/providers/health every 60 seconds. Amber = watch,
 * red-pulsing = provider error, terracotta = all healthy.
 */
export default function AdminToggle() {
  const { user } = useAuth();
  const location = useLocation();
  const [health, setHealth] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!user?.is_admin) return undefined;
    let mounted = true;
    const tick = async () => {
      try {
        const r = await fetchProvidersHealth();
        if (mounted) setHealth(r);
      } catch {
        // fail silently — the dot just stays neutral
      }
    };
    tick();
    timerRef.current = setInterval(tick, 60_000);
    return () => {
      mounted = false;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [user?.is_admin]);

  if (!user?.is_admin) return null;

  const onAdmin = location.pathname.startsWith("/admin");
  const overall = health?.overall_status || "ok";
  const worstId = health?.worst?.id;
  const title = worstId
    ? `${HEALTH_LABELS[overall]} · ${worstId}`
    : HEALTH_LABELS[overall];

  return (
    <div className="inline-flex items-center gap-3">
      <Link
        to="/admin/providers"
        data-testid="admin-health-dot"
        aria-label={title}
        title={title}
        className="inline-flex items-center justify-center h-11 w-6 group"
      >
        <span
          className={`h-2.5 w-2.5 rounded-full transition-all ${DOT_STYLES[overall] || DOT_STYLES.ok} group-hover:scale-125`}
        />
      </Link>
      <Link
        to={onAdmin ? "/dashboard" : "/admin"}
        data-testid="admin-toggle"
        className="inline-flex items-center gap-2 border border-terracotta/60 text-terracotta hover:bg-terracotta hover:text-obsidian-900 px-3 py-1.5 font-sans text-[10px] uppercase tracking-[0.2em] transition-colors"
        title={onAdmin ? "Return to your journal" : "Enter admin panel"}
      >
        {onAdmin ? <BookOpen className="h-3 w-3" /> : <Shield className="h-3 w-3" />}
        {onAdmin ? "Journal" : "Admin"}
      </Link>
    </div>
  );
}
